import { motion } from "framer-motion";
import { Reveal, SectionLabel } from "@/components/Reveal";

const LegalShell = ({ label, title, children, testid }) => (
  <div data-testid={testid}>
    <section className="bg-blueprint pt-40 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <SectionLabel>{label}</SectionLabel>
          <h1 className="mt-5 font-display text-5xl sm:text-7xl font-extrabold uppercase text-white leading-[0.9]">
            {title}
          </h1>
        </motion.div>
      </div>
    </section>
    <section className="pb-24 pt-4">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">{children}</div>
    </section>
  </div>
);

const Block = ({ title, children, testid }) => (
  <Reveal>
    <div className="bg-[#111318] border border-white/10 rounded-md p-7 sm:p-9" data-testid={testid}>
      {title && (
        <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase text-[#FFE600] mb-4">{title}</h2>
      )}
      <div className="text-gray-300 text-sm sm:text-base leading-relaxed space-y-3">{children}</div>
    </div>
  </Reveal>
);

const Impressum = () => (
  <LegalShell label="Rechtliches" title={<>Impressum <span className="text-[#FFE600]">&amp; AGB</span></>} testid="impressum-page">
    <Block title="Impressum" testid="impressum-block">
      <p className="text-gray-500 text-xs uppercase tracking-[0.2em] font-mono2">Angaben gemäß § 5 TMG</p>
      <p>
        <strong className="text-white">HeroPhone</strong>
        <br />
        Ricardo Galindo Cuenca
        <br />
        Dorfstraße 70A
        <br />
        21775 Odisheim
        <br />
        Deutschland
      </p>
      <p>
        Telefon: 0151 16012024
        <br />
        E-Mail: info@hero-phone.de
      </p>
      <p>
        Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV:
        <br />
        Ricardo Galindo Cuenca, Anschrift wie oben
      </p>
      <p>Umsatzsteuer-ID gemäß §27a Umsatzsteuergesetz: (keine vorhanden)</p>
    </Block>

    <Block title="Allgemeine Geschäftsbedingungen (AGB)" testid="agb-intro-block">
      <p>
        <strong className="text-white">HeroPhone – Handy Reparaturen</strong>
        <br />
        HeroPhone · Inhaber: Ricardo Galindo Cuenca
      </p>
    </Block>

    <Block title="1. Geltungsbereich" testid="agb-block-1">
      <p>
        Diese Allgemeinen Geschäftsbedingungen gelten für alle Reparatur- und
        Serviceleistungen der Firma HeroPhone gegenüber ihren Kunden.
      </p>
      <p>
        Mit der Abgabe eines Gerätes zur Reparatur oder der Beauftragung einer
        Dienstleistung erkennt der Kunde diese AGB an.
      </p>
    </Block>

    <Block title="2. Reparaturauftrag" testid="agb-block-2">
      <p>Der Kunde bestätigt mit der Übergabe des Gerätes:</p>
      <ul className="list-disc list-inside space-y-1 text-gray-400">
        <li>Eigentümer des Gerätes zu sein oder zur Reparatur berechtigt zu sein</li>
        <li>dass das Gerät keine Diebstahlware ist</li>
        <li>dass alle wichtigen Daten vorab gesichert wurden</li>
      </ul>
    </Block>

    <Block title="3. Datensicherung" testid="agb-block-3">
      <p>
        Der Kunde ist selbst für die Sicherung seiner Daten verantwortlich.
        HeroPhone übernimmt keine Haftung für Datenverlust, der während Diagnose
        oder Reparatur entstehen kann.
      </p>
      <p>Dies gilt insbesondere für: Fotos, Kontakte, Apps, Systemdaten.</p>
    </Block>

    <Block title="4. Kostenvoranschlag" testid="agb-block-4">
      <p>Ein Kostenvoranschlag wird nach bestem Wissen erstellt.</p>
      <p>
        Sollten während der Reparatur weitere Schäden festgestellt werden, wird
        der Kunde vor Durchführung weiterer Arbeiten informiert.
      </p>
    </Block>

    <Block title="5. Reparaturdauer" testid="agb-block-5">
      <p>
        Die Dauer einer Reparatur hängt ab von: Ersatzteilverfügbarkeit,
        Schadensumfang und Gerätezustand.
      </p>
      <p>Zeitangaben sind daher unverbindlich.</p>
    </Block>

    <Block title="6. Ersatzteile" testid="agb-block-6">
      <p>HeroPhone verwendet je nach Verfügbarkeit:</p>
      <ul className="list-disc list-inside space-y-1 text-gray-400">
        <li>Originalersatzteile oder qualitativ gleichwertige Ersatzteile</li>
        <li>
          auf Anfrage auch minderwertige Qualität – diese wird dann von der
          Garantie ausgeschlossen
        </li>
      </ul>
    </Block>

    <Block title="7. Garantie auf Reparaturen" testid="agb-block-7">
      <p>
        HeroPhone gewährt auf durchgeführte Reparaturen eine 90-Tage
        Geld-zurück-Garantie, sofern ein Fehler der durchgeführten Reparatur
        vorliegt.
      </p>
      <p>
        Innerhalb dieser Zeit wird eine kostenlose Nachbesserung durchgeführt
        oder der Reparaturpreis erstattet.
      </p>
      <p>Die Garantie gilt ausschließlich für das ersetzte Bauteil und die durchgeführte Reparatur.</p>
    </Block>

    <Block title="8. Garantieausschluss" testid="agb-block-8">
      <p>Die Garantie entfällt bei:</p>
      <ul className="list-disc list-inside space-y-1 text-gray-400">
        <li>Sturzschäden</li>
        <li>Wasserschäden oder Feuchtigkeit</li>
        <li>unsachgemäßer Nutzung</li>
        <li>eigenständigen Reparaturversuchen</li>
        <li>Reparaturen durch Dritte</li>
        <li>neuen Schäden am Gerät</li>
        <li>Bestellung von minderer Qualität</li>
      </ul>
    </Block>

    <Block title="9. Wasserschutz / IP-Zertifizierung" testid="agb-block-9">
      <p>
        Viele moderne Smartphones verfügen über eine Wasser- oder
        Staubschutz-Zertifizierung (z. B. IP67 / IP68).
      </p>
      <p>
        Durch das Öffnen des Gerätes kann diese Schutzfunktion nicht garantiert
        erhalten bleiben. HeroPhone übernimmt daher keine Haftung für
        Wasserschäden nach einer Reparatur.
      </p>
    </Block>

    <Block title="10. Display- und Glasbruchrisiko" testid="agb-block-10">
      <p>
        Bei Geräten mit bereits bestehenden Schäden (z. B. Glasbruch,
        Gehäuseverzug oder Mikrorissen) kann während der Reparatur ein weiteres
        Brechen des Displays auftreten.
      </p>
      <p>
        Dieses Risiko liegt außerhalb der Haftung von HeroPhone, sofern der
        Schaden nicht durch grobe Fahrlässigkeit verursacht wurde.
      </p>
    </Block>

    <Block title="11. FaceID / TouchID / Fingerprint" testid="agb-block-11">
      <p>
        Bei bestimmten Geräten (z. B. Apple iPhone) können nach Reparaturen
        Funktionen wie FaceID, TouchID oder der Fingerabdrucksensor durch
        Herstellerschutzmechanismen eingeschränkt werden.
      </p>
      <p>
        HeroPhone übernimmt hierfür keine Garantie, sofern dies technisch durch
        den Hersteller begründet ist.
      </p>
    </Block>

    <Block title="12. Diagnose" testid="agb-block-12">
      <p>
        Für Diagnoseleistungen kann eine Diagnosegebühr erhoben werden, wenn
        keine Reparatur durchgeführt wird.
      </p>
    </Block>

    <Block title="13. Abholung des Gerätes" testid="agb-block-13">
      <p>
        Reparierte Geräte sollten innerhalb von 30 Tagen nach Fertigstellung
        abgeholt werden.
      </p>
      <p>
        HeroPhone behält sich vor, nicht abgeholte Geräte nach angemessener Frist
        einzulagern oder Entsorgungskosten zu berechnen.
      </p>
    </Block>

    <Block title="14. Haftung" testid="agb-block-14">
      <p>
        HeroPhone haftet nur für Schäden, die durch vorsätzliches oder grob
        fahrlässiges Verhalten entstanden sind.
      </p>
      <p>
        Eine Haftung für Folgeschäden oder Datenverlust wird ausgeschlossen,
        soweit gesetzlich zulässig.
      </p>
    </Block>

    <Block title="15. Schlussbestimmungen" testid="agb-block-15">
      <p>
        Sollte eine Bestimmung dieser AGB unwirksam sein, bleibt die Wirksamkeit
        der übrigen Bestimmungen unberührt.
      </p>
      <p>Es gilt deutsches Recht.</p>
    </Block>
  </LegalShell>
);

export default Impressum;
